import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as c from './conf.mjs'
import * as um from './util-misc.mjs'
import * as ue from './util-err.mjs'
import * as ut from './util-text.mjs'

// Short for "mixin: observable".
export function MixObs(cls) {
  return class MixObs extends cls {
    constructor(...val) {
      super(...val)
      return ob.obs(this)
    }
  }
}

/*
Short for "struct recursive". Converts any unknown field to `StructRec` if the
value of that field is a dict. "Recursive" means that arbitrarily nested dicts
become arbitrarily nested instances of `StructRec`. This allows deep mutation:

  const tar = new StructRec({one: {two: 20}})
  t.eq(tar, {one: {two: 20}})

  tar.mut({one: {three: {four: 30}}})
  t.eq(tar, {one: {two: 20, three: {four: 30}}})
*/
export class StructRec extends a.StructLax {
  /*
  Automatically invoked by `a.StructLax` when creating or mutating instances of
  this class, for any fields not explicitly defined via struct spec.

  This explicitly uses generic `StructRec` rather than `this.constructor`
  because in subclasses, descendant structures typically don't want to use
  the same class as the ancestor structure.
  */
  static any(val) {return a.isDict(val) ? new StructRec(val) : val}
}

// TODO convert our models from `a.StructLax` to `a.Struct`.
export class Model extends a.StructLax {
  pk() {return this.id}
  sitePath() {return this.constructor.sitePath?.(this.pk())}
  // Automatically invoked by `a.StructLax` to validate each instance.
  static validate = a.pk
}

export class ObsModel extends MixObs(Model) {}
export class BukModel extends um.MixBuketed(Model) {}
export class BukObsModel extends MixObs(BukModel) {}

// A dict where values "always exist", because they are created on demand.
export class Maker extends a.Bmap {
  // Override in subclass.
  make(inp) {return inp}

  get(inp) {
    const key = a.jsonEncode(inp)
    if (!this.has(key)) this.set(key, this.make(inp))
    return super.get(key)
  }

  clear() {
    a.each(this, ob.deinit)
    super.clear()
  }

  deinit() {this.clear()}
}

export class FunMaker extends Maker {
  constructor(make) {
    super()
    a.priv(this, `make`, a.reqFun(make))
  }
}

/*
When possible, mutate existing values instead of replacing them.
Useful for collections of observable objects.
*/
export class MutColl extends a.ClsColl {
  set(key, val) {
    const tar = super.get(key)
    if (a.isMut(tar)) tar.mut(val)
    else super.set(key, val)
    return this
  }

  // Minor override for more sensible behavior. TODO move to library code.
  addOpt(val) {return a.isSome(val) ? super.addOpt(this.make(val)) : this}

  // The input must be from `FeedPage..getItems`.
  fromItems(src) {return a.arr(src).map(this.getReqByItem, this)}

  // The input should be a page item. See `BukPage`.
  getReqByItem(src) {return this.getReq(um.pk(src))}

  // TODO better name. Short for "get required".
  getReq(key) {return super.get(key) || a.panic(this.errNotFound(key))}

  // Returns `undefined` if key not found.
  getOpt(key) {return super.get(key)}

  errNotFound(key) {
    return new ue.ErrNotFound(`missing entry for ${a.show(key)} in ${this.constructor.name}`)
  }
}

export class ModelColl extends MutColl {get cls() {return Model}}

// Method `.mut` behaves exactly like method `.reset`. WTB better name.
export class ResetModelColl extends ModelColl {
  mut(src) {return this.clear(), super.mut(src)}
}

/*
Should be used for entity types that use `MixBukVersioned`. Assigning
`.pageItem` to entities is a questionable hack used for compatibility with UI
code written for non-buket APIs. This needs to be revised in the future.

When it's not a `c.PUBLIC_RELEASE` check whether requested key is present in
collection or not. If not throw an error.
*/
export class BukModelColl extends ModelColl {
  getReqByItem(src) {
    const out = super.getReqByItem(src)
    out.pageItem = src
    return out
  }

  get(key) {
    return c.PUBLIC_RELEASE ? this.getOpt(key) : a.vac(key) && this.getReq(key)
  }

  // Suboptimal but not our bottleneck. TODO proper indexing.
  getByOrigin(key) {
    const val = this.getByOriginOpt(key)
    if (!val && !c.PUBLIC_RELEASE) throw this.errNotFound(key)
    return val
  }

  getByOriginOpt(key) {
    for (const val of this) if (val.originId === key) return val
    return undefined
  }
}

/*
Variant of `a.Bset` where subclasses can override `.make()` to implement
validation or conversion. TODO consider moving this feature to `@mitranim/js`.
Seems slightly more fundamental than `a.ClsSet` and could be its superclass.
*/
export class Bset extends a.Bset {
  make(val) {return val}
  add(val) {return super.add(this.make(val))}
}

export class PkSet extends Bset {
  make(val) {return a.reqPk(val)}
}

export function MixDedupMap(cls) {
  return class MixDedupMapCls extends cls {
    set(key, val) {
      if (this.has(key)) throw Error(`redundant entry ${a.show(key)} = ${a.show(val)} in ${a.show(this)}`)
      return super.set(key, val)
    }
  }
}

// TODO better name.
export class PathColl extends MixDedupMap(a.Coll) {
  getKeyOpt(val) {return um.getPath(val)}

  current(path) {
    return (
      this.currentExact(path) ||
      a.find(this, val => a.isSubpath(val.path, path))
    )
  }

  currentExact(path) {return this.get(path)}

  static of(...val) {return new this(a.compact(val))}
}

/*
Tool for defining collections with multiple indexes. For an example, see
`ClsMultiColl_ByIdOrPk_BySlug`.

TODO better name. This is not a subclass of `a.Coll` and currently does not
support deletion.

TODO consider converting this to a subclass of `a.ClsColl` with support for
secondary indexes, which would allow to support deletion.
*/
export class ClsMultiColl extends a.ClsVec {
  constructor(src) {
    super()
    this.indexes = this.makeIndexes()
    this.mut(src)
  }

  // Indexes must be instances of `a.ClsColl`.
  makeIndexes() {return []}

  add(val) {
    val = this.make(val)
    // Motive for using `.addOpt` here: for some entities, the additional unique
    // fields on which we build additional indexes can be nullable. For example,
    // `Person..slug` may be null.
    for (const ind of this.indexes) ind.addOpt(val)
    return super.add(val)
  }

  has(key) {
    const inds = this.indexes
    if (inds) for (const ind of inds) if (ind.has(key)) return true
    return false
  }

  get(key) {
    const inds = this.indexes
    if (inds) for (const ind of inds) if (ind.has(key)) return ind.get(key)
    return undefined
  }

  clear() {
    const inds = this.indexes
    if (inds) for (const ind of inds) ind.clear()
    return super.clear()
  }

  // Questionable. Just feed `a.filter` into `new`.
  filter(fun) {
    const tar = new this.constructor()
    for (const val of this) if (fun.call(val, val)) tar.add(val)
    return tar
  }
}

/*
Difference from `a.ClsColl`: automatically tries to use `.id` as primary key,
even if the element class doesn't implement the `.pk` method.
*/
export class ClsCollByPk extends a.ClsColl {
  getKeyOpt(val) {return um.pk(val)}
}

/*
Difference from `a.ClsColl`: automatically tries to use `.slug` as primary key,
even if the element class doesn't implement the `.pk` method.
*/
export class ClsCollBySlug extends a.ClsColl {
  getKeyOpt(val) {return um.getSlug(val)}
}

/*
Collection of objects with multiple indexes: by id and slug. Instances can be
efficiently accessed by any of the available indexes.
*/
export class ClsMultiColl_ByIdOrPk_BySlug extends ClsMultiColl {
  makeIndexes() {return [new ClsCollByPk(), new ClsCollBySlug()]}
}

export class SlugSet extends a.TypedSet {
  reqVal(val) {return a.reqValidStr(val)}
}

// Placed here because it's used by other util code such as `util-md.mjs`.
export class GalleryEntry extends a.Struct {
  static spec = {
    id: ut.laxRndId,
    src: a.optStr, // One of these must be provided.
    name: a.optStr, // One of these must be provided.
  }

  pk() {return this.id}
  valid() {return a.vac(this.src || this.name) && this}
  getSrc(width) {return this.src || ut.imageNameToUrl(this.name, width)}
}

/*
Goals:

  * good "push"
  * good "has"
  * good "delete"
  * good reverse iteration

Alternatively (not implemented):

  * good "unshift"
  * good forward iteration

For comparison, `Array`:

  * good "push"
  * bad "unshift"
  * bad "has"
  * bad "delete"
  * good forward iteration
  * good reverse iteration

For comparison, `Set`:

  * good "push"
  * no "unshift"
  * good "has"
  * good "delete"
  * okay forward iteration
  * no reverse iteration
*/
export class ReverseSet extends a.Emp {
  added(val) {
    const set = this.set ??= new Set()
    return !set.has(val) && !(this.act = !set.add(val))
  }

  add(val) {return this.added(val), this}
  has(val) {return !!this.set?.has(val)}
  delete(val) {return !!this.set?.delete(val) && !(this.act = false)}
  clear() {return this.set?.clear(), (this.act = this.arr = undefined), this}

  toArray() {
    if (!this.act) {
      const set = this.set
      this.arr = set ? a.reverseMut([...set]) : []
      this.act = true
    }
    return this.arr
  }

  get size() {return this.set?.size ?? 0}
  [Symbol.iterator]() {return this.toArray().values()}
}
